import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';

export interface ColumnOption<T extends string> {
  label: string;
  value: T;
}

interface RatesReportColumnPickerProps<T extends string> {
  options: ColumnOption<T>[];
  value: T[];
  defaultValue: T[];
  onChange: (value: T[]) => void;
}

export function RatesReportColumnPicker<T extends string>({
  options,
  value,
  defaultValue,
  onChange,
}: RatesReportColumnPickerProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggle = (key: T) => {
    const isSelected = value.includes(key);
    if (isSelected) {
      const next = value.filter((item) => item !== key);
      onChange(next.length > 0 ? next : defaultValue);
      return;
    }
    onChange([...value, key]);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', width: '100%' }}>
      <Button
        type="button"
        icon="pi pi-table"
        label={`${value.length} עמודות`}
        outlined
        severity="secondary"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          justifyContent: 'space-between',
          direction: 'rtl',
          height: '36px',
        }}
      />

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 20,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
            maxHeight: '320px',
            overflowY: 'auto',
            padding: '8px',
            direction: 'rtl',
          }}
        >
          {options.map((option) => {
            const checked = value.includes(option.value);
            const inputId = `rates-col-${option.value}`;
            return (
              <label
                key={option.value}
                htmlFor={inputId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <Checkbox
                  inputId={inputId}
                  checked={checked}
                  onChange={() => toggle(option.value)}
                />
                <span style={{ fontSize: '0.9rem', color: '#374151' }}>
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
